import { ChangeDetectionStrategy, Component, computed, effect, inject, input, linkedSignal, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { HttpService } from 'src/app/services/http.service';
import { ConfigurationSaisieDonneesAnalyse } from '../../../bo/configurationSaisieDonneesAnalyse';
import { DonneeAnalyse } from '../../../bo/donneeAnalyse';
import { Projet } from '../../../bo/projet';
import { FonctionsService } from '../../../services/fonctions-partagees.service';
import { KpiService } from '../../../services/kpi.service';
import { TraductionService } from '../../../services/traduction.service';
import { UtilisateurService } from '../../../services/utilisateur.service';
import { AccessPermissionType } from 'src/app/bo/accessPermisionType';
import { KeycloakUtilisateurService } from 'src/app/services/keycloak/keycloak-utilisateur.service';
import { ItemSortHelper } from 'src/app/utils/itemSortHelper';
import { SharedModule } from '../../../shared/shared.module';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { PopupComponent } from '@libs/front/components/popup/popup.component';
import { DataTableComponent, TableCellDirective, TableColumn } from '@libs/front/components/data-table/data-table.component';
import { ToggleComponent } from '@libs/front/components/toggle/toggle.component';
import { InputComponent } from '@libs/front/components/input/input.component';

export enum TypeParent {
  Projet,
  Tir,
  Noeud
}

@Component({
  selector: 'app-blast-kpi',
  templateUrl: './blast-kpi.component.html',
  styleUrls: ['./blast-kpi.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, ButtonComponent, PopupComponent, DataTableComponent, TableCellDirective, ToggleComponent, InputComponent]
})
export class BlastKpiComponent {
  tabConfigSaisie       = input<ConfigurationSaisieDonneesAnalyse[]>();
  tabDonneesAnalyse     = input<DonneeAnalyse[]>();
  donneesChargees       = input<boolean>();
  estProjet             = input<boolean>();
  estTir                = input<boolean>();
  tirId                 = input<number>();
  typeParent            = input<TypeParent>();
  kpiIsVerified         = input<boolean>(false);
  rechargerDonnees      = output<boolean>();
  verifyToggled         = output<boolean>();
  projet                = input<Projet>(null);
  inProjetDetail        = input<boolean>(false);

  private readonly utilService         = inject(UtilisateurService);
  private readonly userProfileService  = inject(KeycloakUtilisateurService);
  private readonly tradService         = inject(TraductionService);
  private readonly fonctionsService    = inject(FonctionsService);
  private readonly translate           = inject(TranslateService);
  private readonly http                = inject(HttpService);
  private readonly kpiService          = inject(KpiService);

  protected readonly langueCourante  = toSignal(this.tradService.langueCourante$, { initialValue: this.tradService.translate.currentLang });
  protected readonly estClient       = computed(() => {
    this.resolvedDonneesChargees();
    return this.userProfileService.isGuest;
  });
  protected readonly estUtilisateur  = computed(() => !this.estClient());
  protected readonly aucuneDonnee    = signal(false);
  protected readonly exportModalOpen = signal(false);
  protected readonly searchTerm      = signal('');
  protected readonly siteCurrency    = signal('');

  TypeParent = TypeParent;
  AccessPermissionEnum = AccessPermissionType;
  kpiEditValue = '';

  // ── Detail popup ─────────────────────────────────────────────────────────────
  readonly kpiDetailOpen          = signal(false);
  readonly kpiDetailDonnee        = signal<DonneeAnalyse | null>(null);
  readonly kpiDetailCfg           = signal<ConfigurationSaisieDonneesAnalyse | null>(null);
  readonly kpiDetailValeurBlast   = signal('');
  readonly kpiDetailValeurManuel  = signal('');
  readonly kpiDetailErreurApi     = signal(false);
  readonly kpiDetailCancelOpen    = signal(false);

  readonly kpiDetailTitle = computed(() => {
    const d = this.kpiDetailDonnee();
    if (!d) return '';
    const nom  = d.referentielDonneeAnalyse?.nom ?? '';
    const unit = d.referentielDonneeAnalyse?.nomUniteReferentiel;
    return nom + (unit ? ` (${unit})` : '');
  });

  readonly kpiDetailTypeLabel = computed(() => {
    this.langueCourante();
    const rawType = this._safeStr(this.kpiDetailCfg()?.referentielDonneeAnalyse?.type);
    if (!rawType) return '';
    return this.translate.instant(rawType).replace(/'/g, '');
  });

  readonly kpiDetailCanEdit = computed(() =>
    this.userProfileService.allRoles.includes('access_all_kpi') ||
    !!this.kpiDetailDonnee()?.autorisePourUtil
  );

  readonly kpiDetailDisplayUnit = computed(() => {
    const d   = this.kpiDetailDonnee();
    const cfg = this.kpiDetailCfg();
    if (!d) return '';
    return this.replaceCurrencyPlaceholder(
      this._safeStr(d.referentielDonneeAnalyse?.displayUnit)
      || this._extractSymbol(cfg?.displayUnit ?? '')
      || this._safeStr(cfg?.referentielDonneeAnalyse?.nomUniteReferentiel)
    );
  });

  // Internal state used when the component self-loads (i.e. called from blast.component with only tirId)
  private readonly _internalTabConfigSaisie  = signal<ConfigurationSaisieDonneesAnalyse[] | undefined>(undefined);
  private readonly _internalTabDonneesAnalyse = signal<DonneeAnalyse[] | undefined>(undefined);
  private readonly _internalDonneesChargees  = signal<boolean>(false);

  readonly resolvedTabConfigSaisie    = computed(() => this.tabConfigSaisie()    ?? this._internalTabConfigSaisie());
  readonly resolvedTabDonneesAnalyse  = computed(() => this.tabDonneesAnalyse()  ?? this._internalTabDonneesAnalyse());
  readonly resolvedDonneesChargees    = computed(() => this.donneesChargees()    ?? this._internalDonneesChargees());

  readonly kpiData = linkedSignal(() => this.resolvedTabDonneesAnalyse() ?? []);

  readonly kpiTableColumns: TableColumn[] = [
    { key: 'nom',        label: 'GLOBAL.NOM',                 width: '25%' },
    { key: 'type',       label: 'GLOBAL.TYPE',                width: '25%' },
    { key: 'finalValue', label: 'CONFIGURATION_DONNEES.FINAL', width: '35%' },
    { key: 'actions',    label: '', sortable: false,          width: '15%' },
  ];

  readonly kpiEditId = signal<number | null>(null);

  readonly kpiTableRows = computed(() => {
    const configs = this.resolvedTabConfigSaisie() ?? [];
    return this.kpiData().map(d => {
      const cfg = configs.find(c => c.referentielDonneeAnalyseId === d.referentielDonneeAnalyseId);

      const computed = d.finalValue
        ? { value: d.finalValue, source: d.sourceFinalValue ?? 'SOURCE_KPI.NOT_SET' }
        : this._computeFinalValueDisplay(d);

      return {
        _id:              d.referentielDonneeAnalyseId,
        _donnee:          d,
        nom:              d.referentielDonneeAnalyse?.nom ?? '',
        description:      this._safeStr(cfg?.referentielDonneeAnalyse?.description) || this._safeStr(d.referentielDonneeAnalyse?.description),
        type:             this._safeStr(cfg?.referentielDonneeAnalyse?.type)        || this._safeStr(d.referentielDonneeAnalyse?.type),
        finalValue:       computed.value,
        displayUnit:      this._safeStr(d.referentielDonneeAnalyse?.displayUnit)
                            || this._extractSymbol(cfg?.displayUnit ?? '')
                            || this._safeStr(cfg?.referentielDonneeAnalyse?.nomUniteReferentiel),
        source:           computed.source,
        typeSaisieKpi:    d.referentielDonneeAnalyse?.typeSaisieKpi ?? '',
        autorisePourUtil: d.autorisePourUtil,
        measuredValue:    d.measuredValue ?? '',
      };
    });
  });

  constructor() {
    this.siteCurrency.set(this.utilService.currentSite()?.currency ?? '');

    effect(() => {
      const tirId = this.tirId();
      if (tirId && !this.tabConfigSaisie()) {
        this._chargerDonneesAnalyse(tirId);
      }
    });
  }

  replaceCurrencyPlaceholder(unit: string): string {
    return unit?.replace('CURRENCY', this.siteCurrency() || '¤') ?? '';
  }

  private _computeFinalValueDisplay(d: DonneeAnalyse): { value: string; source: string } {
    const nom = d.referentielDonneeAnalyse?.nom?.toLowerCase() ?? '';
    const isAzimuth = nom.includes('azimuth') || nom.includes('azimut');

    // Ligne de projet agrégée : le back a déjà totalisé la meilleure valeur de chaque tir
    // dans nodeValue. Les trois autres champs ne portent que des sommes partielles —
    // manualValue ne totalise que les saisies manuelles, et vaut "0" (donc truthy) dès
    // qu'aucun tir n'en a. S'arrêter dessus affichait 0, ou la valeur du seul tir saisi.
    // nodeValue n'est renseigné que sur ces lignes calculées : celles d'un tir gardent
    // la cascade manuel → mesuré → design ci-dessous.
    if (d.nodeValue) {
      return {
        value: isAzimuth
          ? this._kpiFormatValue((Number(d.nodeValue) * 180 / Math.PI).toString())
          : this._kpiFormatValue(d.nodeValue),
        // Un agrégat n'a pas de source unique : chaque tir peut y contribuer par une
        // valeur manuelle, mesurée ou design.
        source: 'SOURCE_KPI.NOT_SET'
      };
    }

    for (const { val, src } of [
      { val: d.manualValue,   src: 'SOURCE_KPI.MANUAL'   },
      { val: d.measuredValue, src: 'SOURCE_KPI.MEASURED'  },
      { val: d.designValue,   src: 'SOURCE_KPI.DESIGN'    },
    ]) {
      if (val) {
        return {
          value: isAzimuth
            ? this._kpiFormatValue((Number(val) * 180 / Math.PI).toString())
            : this._kpiFormatValue(val),
          source: src
        };
      }
    }
    return { value: '', source: 'SOURCE_KPI.NOT_SET' };
  }

  private _extractSymbol(displayUnit: string): string {
    if (!displayUnit) return '';
    const parts = displayUnit.split(' - ');
    return parts.length > 1 ? parts[1].trim() : displayUnit;
  }

  private _safeStr(value: string | undefined): string {
    if (!value || value === 'undefined' || value === "'undefined'") return '';
    return value;
  }

  private _kpiFormatValue(value: string): string {
    const n = parseFloat(value);
    if (isNaN(n)) return value;
    return (Math.abs(n) < 10 ? +n.toFixed(2) : +n.toFixed(0)).toString();
  }

  private _isAzimuthRow(row: any): boolean {
    const nom: string = row.nom?.toLowerCase() ?? '';
    return nom.includes('azimuth') || nom.includes('azimut');
  }

  displayMeasuredForEdit(row: any): string {
    const val = row.measuredValue;
    if (!val) return '';
    return this._isAzimuthRow(row)
      ? this._kpiFormatValue((Number(val) * 180 / Math.PI).toString())
      : this._kpiFormatValue(String(val));
  }

  onKpiRowSelected(row: any): void {
    this.ouvrirModaleModif(row._donnee);
  }

  protected onVerifiedToggleChanged(value: boolean): void {
    this.verifyToggled.emit(value);
  }

  /** Relance le calcul serveur de tous les KPI du tir, puis recharge la liste. */
  protected onRefresh(): void {
    const tirId = this.tirId();
    if (!this.estTir() || !tirId) return;

    this.fonctionsService.loading_screen(true);
    this.kpiService.triggerKpiCalculation(tirId).subscribe({
      next: () => {
        this.fonctionsService.loading_screen(false);
        this.fonctionsService.launch_toast(true);
        // Le composant s'auto-charge quand il n'a pas reçu de config en input ;
        // sinon c'est le parent qui recharge via (rechargerDonnees).
        if (!this.tabConfigSaisie()) {
          this._chargerDonneesAnalyse(tirId);
        } else {
          this.rechargerDonnees.emit(true);
        }
      },
      error: () => {
        this.fonctionsService.loading_screen(false);
        this.fonctionsService.launch_toast(false);
      }
    });
  }

  onKpiEdit(row: any): void {
    if (this.kpiEditId() === row._id) {
      this.kpiEditId.set(null);
      return;
    }
    let val = row._donnee.manualValue ?? '';
    if (this._isAzimuthRow(row) && val) {
      val = this._kpiFormatValue((Number(val) * 180 / Math.PI).toString());
    } else if (val) {
      val = this._kpiFormatValue(val);
    }
    this.kpiEditValue = val;
    this.kpiEditId.set(row._id);
  }

  onKpiValidate(row: any, isReset = false): void {
    const id: number = row._donnee.referentielDonneeAnalyseId;
    let manualValue = '';
    if (!isReset) {
      let val = this.kpiEditValue;
      if (this._isAzimuthRow(row) && val) {
        val = (Number(val) * Math.PI / 180).toString();
      }
      manualValue = this._kpiFormatValue(val);
    }
    this.kpiEditId.set(null);
    this.kpiEditValue = '';
    this._applyOptimisticUpdate(id, { manualValue });
  }

  onKpiCheckboxChange(checked: boolean, row: any): void {
    const id: number = row._donnee.referentielDonneeAnalyseId;
    this._applyOptimisticUpdate(id, { manualValue: checked ? 'Oui' : 'Non' });
  }

  private _applyOptimisticUpdate(id: number, patch: Partial<DonneeAnalyse>): void {
    const current = this.kpiData().find(d => d.referentielDonneeAnalyseId === id);
    if (!current) return;

    const updated: DonneeAnalyse = { ...current, ...patch };
    this._recalcKpiFinalValue(updated);
    this.kpiData.update(list => list.map(d => d.referentielDonneeAnalyseId === id ? updated : d));

    this.kpiService.updateDonneeAnalyse(updated).subscribe({
      next:  () => this.fonctionsService.launch_toast(true),
      error: () => this.fonctionsService.launch_toast(false),
    });
  }

  private _recalcKpiFinalValue(donnee: DonneeAnalyse): void {
    const nom = donnee.referentielDonneeAnalyse?.nom?.toLowerCase() ?? '';
    const isAzimuth = nom.includes('azimuth') || nom.includes('azimut');
    for (const { val, src } of [
      { val: donnee.manualValue,   src: 'SOURCE_KPI.MANUAL'   },
      { val: donnee.measuredValue, src: 'SOURCE_KPI.MEASURED'  },
      { val: donnee.designValue,   src: 'SOURCE_KPI.DESIGN'    },
    ]) {
      if (val) {
        donnee.finalValue = isAzimuth
          ? this._kpiFormatValue((Number(val) * 180 / Math.PI).toString())
          : this._kpiFormatValue(val);
        donnee.sourceFinalValue = src;
        return;
      }
    }
    donnee.finalValue = '';
    donnee.sourceFinalValue = 'SOURCE_KPI.NOT_SET';
  }

  private _chargerDonneesAnalyse(tirId: number): void {
    const siteId = this.utilService.currentSite()?.siteId;
    if (!siteId) return;

    const isGuest = !this.userProfileService.allRoles.includes('access_all_kpi');

    forkJoin([
      this.kpiService.getBlastAnalysisData(tirId),
      this.kpiService.getSiteConfigAnalysisEntry(siteId),
    ]).subscribe(async ([donnees, configs]) => {
      donnees.forEach(donnee => {
        const config = configs.find(c => c.referentielDonneeAnalyseId === donnee.referentielDonneeAnalyseId);
        if (config) {
          donnee.accessPermission = config.accessPermission;
          donnee.isEnabled = config.isEnabled;
          donnee.autorisePourUtil = (isGuest && config.accessPermission === AccessPermissionType.READANDWRITE) || (!isGuest && config.isEnabled);
        }
      });

      donnees.sort((a, b) => {
        if (a.autorisePourUtil === b.autorisePourUtil)
          return a.referentielDonneeAnalyse.nom.localeCompare(b.referentielDonneeAnalyse.nom);
        return a.autorisePourUtil ? -1 : 1;
      });

      try { donnees = await ItemSortHelper.sortKpi(donnees); } catch { }

      this._internalTabConfigSaisie.set(configs);
      this._internalTabDonneesAnalyse.set(donnees);
      this._internalDonneesChargees.set(true);
    });
  }

  ouvrirModaleModif(donneeAnalyse: DonneeAnalyse): void {
    const cfg = this.resolvedTabConfigSaisie()?.find(c => c.referentielDonneeAnalyseId === donneeAnalyse.referentielDonneeAnalyseId) ?? null;
    this.kpiDetailDonnee.set(donneeAnalyse);
    this.kpiDetailCfg.set(cfg);
    this.kpiDetailValeurBlast.set(donneeAnalyse.valeur ?? '');
    this.kpiDetailValeurManuel.set(donneeAnalyse.manualValue ?? '');
    this.kpiDetailErreurApi.set(false);
    this.kpiDetailOpen.set(true);
  }

  onKpiDetailClose(): void {
    const d = this.kpiDetailDonnee();
    const hasChanges = this.kpiDetailValeurBlast() !== (d?.valeur ?? '')
                    || this.kpiDetailValeurManuel() !== (d?.manualValue ?? '');
    if (hasChanges) {
      this.kpiDetailCancelOpen.set(true);
    } else {
      this.kpiDetailOpen.set(false);
    }
  }

  onKpiDetailConfirmCancel(): void {
    this.kpiDetailCancelOpen.set(false);
    this.kpiDetailOpen.set(false);
  }

  onKpiDetailSubmit(): void {
    this.kpiDetailErreurApi.set(false);
    const donnee = this.kpiDetailDonnee();
    if (!donnee) return;

    const id = donnee.referentielDonneeAnalyseId;
    const updated: DonneeAnalyse = {
      ...donnee,
      valeur:       this.kpiDetailValeurBlast(),
      manualValue:  this.kpiDetailValeurManuel(),
    };
    this._recalcKpiFinalValue(updated);

    // Affichage optimiste immédiat dans le tableau.
    this.kpiData.update(list => list.map(d => d.referentielDonneeAnalyseId === id ? updated : d));
    this.kpiDetailOpen.set(false);

    this.fonctionsService.loading_screen(true);
    this.kpiService.updateDonneeAnalyse(updated).subscribe({
      next: () => {
        this.fonctionsService.loading_screen(false);
        this.fonctionsService.launch_toast(true);
        this.rechargerDonnees.emit(true);
      },
      error: () => {
        this.fonctionsService.loading_screen(false);
        this.fonctionsService.launch_toast(false);
      }
    });
  }

  lancerExport(): void {
    const startLoader = setTimeout(() => this.fonctionsService.loading_screen(true), 300);
    this.aucuneDonnee.set(false);
    this.http.get<boolean>('Projects/' + this.projet().projetId + '/GotAnalysisData').subscribe({
      next: resultat => {
        clearTimeout(startLoader);
        this.fonctionsService.loading_screen(false);
        if (resultat) this.exportDonneeAnalyse();
        else {
          this.aucuneDonnee.set(true);
          this.exportModalOpen.set(true);
        }
      },
      error: () => {
        clearTimeout(startLoader);
        this.fonctionsService.loading_screen(false);
      },
    });
  }

  exportDonneeAnalyse(): void {
    const startLoader = setTimeout(() => this.exportModalOpen.set(true), 300);

    this.http.downloadFile('Projects/' + this.projet().projetId + '/ExportAnalysisData').subscribe({
      next: (resultat: ArrayBuffer) => {
        const blob = new Blob([resultat], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.projet().nom + '.csv';
        document.body.appendChild(a);
        a.click();
        this.exportModalOpen.set(false);
        clearTimeout(startLoader);
        setTimeout(() => a.remove(), 100);
      },
      error: () => {
        this.translate.get('GLOBAL.ERREUR_RECUP_DONNEES').subscribe(trad => {
          this.fonctionsService.launch_toast(false, false, trad);
        });
        this.exportModalOpen.set(false);
        clearTimeout(startLoader);
      },
    });
  }
}
